'use client';

import { c as _c } from "react/compiler-runtime";
import { Combobox as ChakraCombobox, Portal } from '@chakra-ui/react';
import * as React from 'react';
import { CloseButton } from './close-button';
interface ComboboxControlProps extends ChakraCombobox.ControlProps {
  clearable?: boolean;
}
export const ComboboxControl = React.forwardRef<HTMLDivElement, ComboboxControlProps>(function ComboboxControl(props, ref) {
  const $ = _c(14);
  let children;
  let clearable;
  let rest;
  if ($[0] !== props) {
    ({
      children,
      clearable,
      ...rest
    } = props);
    $[0] = props;
    $[1] = children;
    $[2] = clearable;
    $[3] = rest;
  } else {
    children = $[1];
    clearable = $[2];
    rest = $[3];
  }
  let t0;
  if ($[4] !== clearable) {
    t0 = clearable && <ComboboxClearTrigger />;
    $[4] = clearable;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  let t1;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ChakraCombobox.Trigger />;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== t0) {
    t2 = <ChakraCombobox.IndicatorGroup>{t0}{t1}</ChakraCombobox.IndicatorGroup>;
    $[7] = t0;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== children || $[10] !== ref || $[11] !== rest || $[12] !== t2) {
    t3 = <ChakraCombobox.Control {...rest} ref={ref}>{children}{t2}</ChakraCombobox.Control>;
    $[9] = children;
    $[10] = ref;
    $[11] = rest;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
});
const ComboboxClearTrigger = React.forwardRef<HTMLButtonElement, ChakraCombobox.ClearTriggerProps>(function ComboboxClearTrigger(props, ref) {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <CloseButton focusRingWidth="2px" focusVisibleRing="inside" pointerEvents="auto" size="xs" variant="plain" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props || $[2] !== ref) {
    t1 = <ChakraCombobox.ClearTrigger asChild={true} {...props} ref={ref}>{t0}</ChakraCombobox.ClearTrigger>;
    $[1] = props;
    $[2] = ref;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
});
interface ComboboxContentProps extends ChakraCombobox.ContentProps {
  portalled?: boolean;
  portalRef?: React.RefObject<HTMLElement | null>;
}
export const ComboboxContent = React.forwardRef<HTMLDivElement, ComboboxContentProps>(function ComboboxContent(props, ref) {
  const $ = _c(11);
  let portalRef;
  let rest;
  let t0;
  if ($[0] !== props) {
    ({
      portalled: t0,
      portalRef,
      ...rest
    } = props);
    $[0] = props;
    $[1] = portalRef;
    $[2] = rest;
    $[3] = t0;
  } else {
    portalRef = $[1];
    rest = $[2];
    t0 = $[3];
  }
  const portalled = t0 === undefined ? true : t0;
  const t1 = !portalled;
  let t2;
  if ($[4] !== ref || $[5] !== rest) {
    t2 = <ChakraCombobox.Positioner><ChakraCombobox.Content {...rest} ref={ref} /></ChakraCombobox.Positioner>;
    $[4] = ref;
    $[5] = rest;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== portalRef || $[8] !== t1 || $[9] !== t2) {
    t3 = <Portal container={portalRef} disabled={t1}>{t2}</Portal>;
    $[7] = portalRef;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
});
export const ComboboxItem = React.forwardRef<HTMLDivElement, ChakraCombobox.ItemProps>(function ComboboxItem(props, ref) {
  const $ = _c(10);
  let children;
  let item;
  let rest;
  if ($[0] !== props) {
    ({
      item,
      children,
      ...rest
    } = props);
    $[0] = props;
    $[1] = children;
    $[2] = item;
    $[3] = rest;
  } else {
    children = $[1];
    item = $[2];
    rest = $[3];
  }
  let t0;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <ChakraCombobox.ItemIndicator />;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== children || $[6] !== item || $[7] !== ref || $[8] !== rest) {
    t1 = <ChakraCombobox.Item key={item.value} item={item} {...rest} ref={ref}>{children}{t0}</ChakraCombobox.Item>;
    $[5] = children;
    $[6] = item;
    $[7] = ref;
    $[8] = rest;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  return t1;
});
export const ComboboxRoot = React.forwardRef<HTMLDivElement, ChakraCombobox.RootProps>(function ComboboxRoot(props, ref) {
  const $ = _c(6);
  let t0;
  if ($[0] !== props.positioning) {
    t0 = {
      sameWidth: true,
      ...props.positioning
    };
    $[0] = props.positioning;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== props || $[3] !== ref || $[4] !== t0) {
    t1 = <ChakraCombobox.Root {...props} ref={ref} positioning={t0} />;
    $[2] = props;
    $[3] = ref;
    $[4] = t0;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}) as ChakraCombobox.RootComponent;
interface ComboboxItemGroupProps extends ChakraCombobox.ItemGroupProps {
  label: React.ReactNode;
}
export const ComboboxItemGroup = React.forwardRef<HTMLDivElement, ComboboxItemGroupProps>(function ComboboxItemGroup(props, ref) {
  const $ = _c(11);
  let children;
  let label;
  let rest;
  if ($[0] !== props) {
    ({
      children,
      label,
      ...rest
    } = props);
    $[0] = props;
    $[1] = children;
    $[2] = label;
    $[3] = rest;
  } else {
    children = $[1];
    label = $[2];
    rest = $[3];
  }
  let t0;
  if ($[4] !== label) {
    t0 = <ChakraCombobox.ItemGroupLabel>{label}</ChakraCombobox.ItemGroupLabel>;
    $[4] = label;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  let t1;
  if ($[6] !== children || $[7] !== ref || $[8] !== rest || $[9] !== t0) {
    t1 = <ChakraCombobox.ItemGroup {...rest} ref={ref}>{t0}{children}</ChakraCombobox.ItemGroup>;
    $[6] = children;
    $[7] = ref;
    $[8] = rest;
    $[9] = t0;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  return t1;
});
export const ComboboxLabel = ChakraCombobox.Label;
export const ComboboxInput = ChakraCombobox.Input;
export const ComboboxEmpty = ChakraCombobox.Empty;
export const ComboboxItemText = ChakraCombobox.ItemText;